import { orderBy } from "es-toolkit";
import { type ReactNode, useCallback, useState } from "react";
import { startViewTransition } from "use-start-view-transition";

import { useCommentThreadContext } from "@/components/app/post/post-comments/comment-thread-context";
import { Button } from "@/components/ui/button";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { useToast } from "@/components/ui/use-toast";
import { PostContextProvider, usePostContext } from "@/contexts/post-context";
import type { Post } from "@/graphql/codegen/graphql";
import { useGetCurrentUserPostStats } from "@/hooks-api/use-get-current-user-post-stats";
import { isAbortError } from "@/utils/helpers";
import { cn } from "@/utils/lib";

import { CommentContent } from "./comment-content";

type PostCommentItemProps = {
  nestingLevel: number;
  loadMoreRepliesForComment?: (postHash: string) => Promise<void>;
  isLastItem: boolean;
  isFirstItem?: boolean;
  children?: ReactNode;
  postCreatorPublicKey: string;
  parentMeta?: Record<
    number,
    { isCurrentChildLastItem: boolean; total: number; postHash?: string }
  >;
};

export const PostCommentItem = ({
  nestingLevel,
  loadMoreRepliesForComment,
  isLastItem,
  isFirstItem,
  postCreatorPublicKey,
  parentMeta = {},
  children,
}: PostCommentItemProps) => {
  const {
    post,
    post: { replies },
    postHash,
    updatePost,
  } = usePostContext();
  const { hoveredLine, setHoveredLine, setCommentState, commentState } =
    useCommentThreadContext();

  const { whaleViewCount, viewCount } = useGetCurrentUserPostStats(post);

  const [loadingMore, setLoadingMore] = useState<number[]>([]);
  const { toast } = useToast();
  const MAX_LEVEL_TO_SHOW = 4;

  let comments = replies?.nodes?.filter(Boolean) ?? [];

  comments = orderBy(
    comments,
    [
      (comment: (typeof comments)["0"]) =>
        comment.poster?.publicKey === postCreatorPublicKey, // This will sort by true (post creator comments) first
    ],
    ["desc", "asc"],
  );

  const hasMoreComments = (replies?.totalCount ?? 0) > comments.length;
  const showSubComments =
    nestingLevel <= MAX_LEVEL_TO_SHOW && comments.length > 0;
  const showMoreCommentsButton =
    (nestingLevel > MAX_LEVEL_TO_SHOW && (replies?.totalCount || 0) > 0) ||
    (!showSubComments && hasMoreComments);

  const onLoadMoreClick = useCallback(
    (index: number) => async () => {
      if (!postHash) {
        throw new Error("Post hash is required for a comment");
      }

      try {
        setLoadingMore((prev) => Array.from(new Set([...prev, index])));
        if (loadMoreRepliesForComment) {
          await loadMoreRepliesForComment(postHash);
        }
      } catch (e) {
        if (!isAbortError(e)) {
          toast({
            variant: "destructive",
            title: "Error",
            description: `An error happened when fetching comments`,
            details: createToastErrorDetails(e),
          });
        }
      } finally {
        setLoadingMore((prev) => prev.filter((i) => i !== index));
      }
    },
    [loadMoreRepliesForComment, postHash, toast],
  );

  const handleToggleChildrenClick = () => {
    startViewTransition(() => {
      setCommentState((prev) => ({
        ...prev,
        [postHash]: {
          ...prev?.[postHash],
          childrenCollapsed: !prev?.[postHash]?.childrenCollapsed,
        },
      }));
    });
  };

  const handleToggleSelfClick = (hash = postHash) => {
    startViewTransition(() => {
      setCommentState((prev) => ({
        ...prev,
        [hash]: {
          ...prev?.[hash],
          selfCollapsed: !prev?.[hash]?.selfCollapsed,
        },
      }));
    });
  };

  const countCommentsRecursively = (comments: Post[]): number => {
    if (!comments || comments.length === 0) {
      return 0;
    }

    return comments.reduce((total: number, comment: Post) => {
      return (
        total +
        1 +
        countCommentsRecursively(comment.replies?.nodes?.filter(Boolean) ?? [])
      );
    }, 0);
  };

  if (!postHash) {
    throw new Error("Post hash is required for a comment");
  }

  return (
    <>
      <div
        className={cn(
          `
            unforce-radius relative w-full px-4 pt-4 pb-2
            md:ml-0
          `,
          {
            "border-t border-border-light": nestingLevel === 1 && !isFirstItem,
          },
        )}
        data-post-hash={postHash}
      >
        <CommentContent
          {...{
            postCreatorPublicKey,
            viewsCount: viewCount!,
            whaleViewsCount: whaleViewCount,
            hideActions: false,
            hideEmoji: false,
            childrenCollapsed: commentState?.[postHash]?.childrenCollapsed,
            selfCollapsed: commentState?.[postHash]?.selfCollapsed,
            commentsCount:
              // @ts-expect-error - This is a recursive function
              countCommentsRecursively(replies?.nodes?.filter(Boolean) ?? []) ??
              0,
            showCollapseButton:
              !showMoreCommentsButton && Number(replies?.nodes?.length) > 0,
            isLastItem,
            nestingLevel,
            hasChildren: comments?.length > 0,
            parentMeta,
            hoveredLine,
            clickable: true,
            showMoreCommentsButton,
            onToggleChildrenClick: handleToggleChildrenClick,
            onToggleSelfClick: handleToggleSelfClick,
            onLineEnter: (postHash: string, lineLevel: number) =>
              setHoveredLine({ postHash, lineLevel }),
            onLineLeave: () => setHoveredLine(null),
            onLineClick: handleToggleSelfClick,
          }}
        />
      </div>
      {!commentState?.[postHash]?.childrenCollapsed &&
        !commentState?.[postHash]?.selfCollapsed && (
          <>
            {showSubComments &&
              comments?.map((comment, i) => (
                <PostContextProvider
                  key={comment.postHash}
                  {...{
                    post: comment,
                  }}
                  updatePostByHash={updatePost}
                >
                  <PostCommentItem
                    {...{
                      loadMoreRepliesForComment,
                      comment,
                      postCreatorPublicKey,
                    }}
                    nestingLevel={nestingLevel + 1}
                    parentMeta={{
                      ...parentMeta,
                      [nestingLevel]: {
                        isCurrentChildLastItem: i === comments.length - 1,
                        total: comments.length,
                        postHash: postHash,
                      },
                    }}
                    isLastItem={i === comments.length - 1}
                  >
                    {hasMoreComments && (
                      <div className="my-2 flex items-center justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={onLoadMoreClick(i)}
                          disabled={loadingMore.includes(i)}
                          className="gap-2"
                        >
                          {loadingMore.includes(i)
                            ? "Loading..."
                            : "Load more replies"}
                        </Button>
                      </div>
                    )}
                  </PostCommentItem>
                </PostContextProvider>
              ))}
          </>
        )}
      {children}
    </>
  );
};
